import { useCallback } from 'react';
import { useIonToast } from '@ionic/react';
import type { ToastOptions } from '@ionic/react';

/**
 * App-wide toast helper (issue #249). Wraps Ionic's `useIonToast` so every toast
 * renders as a Top-Pinned Capsule: `position: 'top'` (below the header toolbar,
 * styled via the `.app-toast` class in index.css) instead of overlapping the
 * bottom call-to-action buttons.
 *
 * Defaults are overridable per call: pass an explicit `position` to override the
 * `top` default (a `position: undefined` falls back to `top` rather than
 * clobbering it), and any caller-supplied `cssClass` is merged with `app-toast`
 * rather than replacing it.
 *
 * `presentAppToast` is memoized so it is safe to use in dependency arrays / as a
 * stable context value.
 */
export function useAppToast() {
  const [present, dismiss] = useIonToast();
  const presentAppToast = useCallback(
    (options: ToastOptions) =>
      present({
        ...options,
        position: options.position ?? 'top',
        cssClass: ['app-toast', options.cssClass]
          .flat()
          .filter(Boolean)
          .join(' '),
      }),
    [present]
  );
  return [presentAppToast, dismiss] as const;
}
